export function timeAgo(dateString: string): string {
  const now = new Date();
  const past = new Date(dateString);

  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  if (diffInSeconds < 60) return 'a few seconds ago';
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }

  const years = Math.floor(diffInSeconds / 31536000);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

export function isValidBetweenDates(
  startDate: Date,
  endDate: Date,
  targetDate: Date | undefined = new Date(),
): boolean {
  return targetDate >= startDate && targetDate <= endDate;
}

export type DateRangeStatus = 'active' | 'available-soon' | 'coming-soon' | 'expired' | 'live';

/**
 * Determines the status of a date range relative to the current time
 * @param startDate - The start date of the range (can be Date, string, or null)
 * @param endDate - The end date of the range (can be Date, string, or null)
 * @param referenceDate - The date to compare against (defaults to current date/time)
 * @returns DateRangeStatus - One of: 'active', 'available-soon', 'coming-soon', 'expired', 'live'
 *
 * Status definitions:
 * - 'live': Has start date but no end date (lifetime partner)
 * - 'active': Current date is within the date range
 * - 'available-soon': Starts within 14 days from now
 * - 'coming-soon': Starts more than 14 days from now OR both dates are null
 * - 'expired': End date has passed
 */
export function getDateRangeStatus(
  startDate: Date | string | null,
  endDate: Date | string | null,
  referenceDate: Date = new Date()
): DateRangeStatus {
  // If both dates are missing, it's coming soon (not yet configured)
  if (!startDate && !endDate) {
    return 'coming-soon';
  }

  // If start date exists but end date is null, it's a lifetime partner (live)
  if (startDate && !endDate) {
    const start = new Date(startDate);
    // Check if the start date has passed
    if (referenceDate >= start) {
      return 'live';
    }
    // If start date is in the future, treat as coming soon
    const millisecondsUntilStart = start.getTime() - referenceDate.getTime();
    const daysUntilStart = millisecondsUntilStart / (1000 * 60 * 60 * 24);

    if (daysUntilStart <= 14) {
      return 'available-soon';
    }

    return 'coming-soon';
  }

  // If only end date exists (no start date), default to coming soon
  if (!startDate && endDate) {
    return 'coming-soon';
  }

  // Both dates exist - standard date range logic
  const start = new Date(startDate);
  const end = new Date(endDate);

  // If expired (now > endDate)
  if (referenceDate > end) {
    return 'expired';
  }

  // If active (now between start and end)
  if (referenceDate >= start && referenceDate <= end) {
    return 'active';
  }

  // If future (now < startDate)
  if (referenceDate < start) {
    // Calculate days until start
    const millisecondsUntilStart = start.getTime() - referenceDate.getTime();
    const daysUntilStart = millisecondsUntilStart / (1000 * 60 * 60 * 24);

    // If starts within 14 days
    if (daysUntilStart <= 14) {
      return 'available-soon';
    }

    return 'coming-soon';
  }

  return 'active';
}